import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { SpreadSelectorV2, CardDrawing, ReadingResult, ReadingGenerationStatus } from '../components/tarot';
import { CardPreloader } from '../components/common/CardPreloader';
import { Button, Input, Card, toast } from '../components/common';
import { TAROT_DECK, TAROT_SPREADS } from '../data/tarotDeck';
import { useCreateReading } from '../hooks/useReadings';
import { useOfflineAuth } from '../hooks/useOfflineAuth';
import { isPreloadComplete } from '../utils/imagePreloader';
import type { TarotSpread, DrawnCard, ReadingSession } from '../types/tarot';
import type { SpreadType } from '../api/spreads';
import { WifiOff } from 'lucide-react';

interface ExtendedReadingSession extends ReadingSession {
  readingId?: string;
  itemDeducted?: boolean;
  selectedSpreadKey?: string; // API spread key for V2 selector
}

export default function ReadingPage() {
  const { isOnline } = useOfflineAuth();
  const navigate = useNavigate();
  const [needsPreload, setNeedsPreload] = useState(!isPreloadComplete());
  const [session, setSession] = useState<ExtendedReadingSession>({
    question: '',
    selectedSpread: TAROT_SPREADS[0],
    drawnCards: [],
    currentStep: 'question',
    isComplete: false
  });
  
  const createReading = useCreateReading();
  
  useEffect(() => {
    // Check if preload is needed on mount
    setNeedsPreload(!isPreloadComplete());
  }, []);
  
  // Show warning if offline
  useEffect(() => {
    if (!isOnline) {
      toast.error('Tarot readings require an internet connection for AI interpretations');
    }
  }, [isOnline]);
  
  const handlePreloadComplete = () => {
    setNeedsPreload(false);
  };
  
  const handleQuestionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (session.question.trim()) {
      setSession(prev => ({ ...prev, currentStep: 'spread' }));
    }
  };
  
  const handleSpreadSelect = (spread: TarotSpread | SpreadType) => {
    // Normalise API SpreadType → legacy TarotSpread shape for CardDrawing
    let legacySpread: TarotSpread;
    if ('spreadKey' in spread) {
      legacySpread = {
        id: spread.spreadKey,
        name: spread.nameEn,
        description: spread.descriptionTh || spread.nameEn,
        cardCount: spread.cardCount,
        positions: (spread.positions ?? []).map((p: any, index: number) => ({
          // DB positions use "position" as string key, not numeric "id"
          id: index + 1,
          name: p.name_en || p.name || p.position || `Position ${index + 1}`,
          description: p.description || '',
          x: p.x ?? (50 + index * 10),
          y: p.y ?? 50,
        })),
      };
    } else {
      legacySpread = spread;
    }

    setSession(prev => ({
      ...prev,
      selectedSpread: legacySpread,
      selectedSpreadKey: 'spreadKey' in spread ? spread.spreadKey : spread.id,
      currentStep: 'drawing',
    }));
  };
  
  const handleDrawingComplete = async (cards: DrawnCard[]) => {
    try {
      console.log('[ReadingPage] handleDrawingComplete called with:', {
        question: session.question,
        spreadId: session.selectedSpread.id,
        spreadName: session.selectedSpread.name,
        selectedSpreadKey: session.selectedSpreadKey,
        cardCount: cards.length,
        cards: cards.map(c => ({ id: c.id, name: c.name, position: c.position, isReversed: c.isReversed }))
      });

      // Create reading with atomic item deduction
      const response = await createReading.mutateAsync({
        question: session.question,
        spreadId: session.selectedSpread.id,
        cards: cards.map(card => ({
          cardId: card.id,
          cardName: card.name,  // Include card name
          position: card.position,
          isReversed: card.isReversed
        }))
      });

      console.log('[ReadingPage] Reading created successfully:', response);

      // Extract reading ID and item deduction status from response
      // The API returns: { success: true, data: { reading: { id, status, itemDeducted } } }
      const readingData = response.data as any;
      const readingId = readingData.reading?.id || readingData.id;
      const itemDeducted = readingData.reading?.itemDeducted || readingData.itemDeducted || false;

      console.log('[ReadingPage] Extracted data:', { readingId, itemDeducted });

      // Update session with reading ID and move to generating step
      setSession(prev => ({
        ...prev,
        drawnCards: cards,
        readingId,
        itemDeducted,
        currentStep: 'generating',
        isComplete: false
      }));

      toast.success(
        itemDeducted 
          ? 'Reading created! Item deducted. Generating interpretation...'
          : 'Reading created! Generating interpretation...'
      );

    } catch (error) {
      console.error('[ReadingPage] Failed to create reading:', error);
      console.error('[ReadingPage] Error details:', {
        message: (error as any)?.message,
        response: (error as any)?.response,
        stack: (error as any)?.stack
      });
      toast.error('Failed to create reading. Please try again.');
    }
  };
  
  const handleGenerationComplete = (readingId: string) => {
    // Move to result step when generation is complete
    setSession(prev => ({
      ...prev,
      currentStep: 'result',
      isComplete: true
    }));
  };
  
  const handleGenerationError = (error: string) => {
    // Show error but still allow viewing the reading
    toast.error(`AI generation failed: ${error}`);
    
    // Move to result step even on error (reading is saved)
    setSession(prev => ({
      ...prev,
      currentStep: 'result',
      isComplete: true
    }));
  };
  
  const handleSaveReading = async (_interpretation: string, _tags?: string[]) => {
    // Reading is already saved during creation, this is just for UI consistency
    // In the atomic flow, the reading is created immediately with item deduction
    // and the AI interpretation is generated asynchronously
    try {
      toast.success('Reading is already saved in your history!');
      navigate('/history');
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };
  
  const handleNewReading = () => {
    setSession({
      question: '',
      selectedSpread: TAROT_SPREADS[0],
      drawnCards: [],
      currentStep: 'question',
      isComplete: false,
      readingId: undefined,
      itemDeducted: undefined,
      selectedSpreadKey: undefined
    });
  };
  
  const handleBackToSpread = () => {
    setSession(prev => ({ ...prev, currentStep: 'spread' }));
  };
  
  const handleBackToQuestion = () => {
    setSession(prev => ({ ...prev, currentStep: 'question' }));
  };
  
  // Show preloader if needed
  if (needsPreload) {
    return <CardPreloader onComplete={handlePreloadComplete} />;
  }
  
  // Show offline message if somehow accessed offline
  if (!isOnline) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto text-center p-8">
            <div className="w-16 h-16 mx-auto bg-yellow-900/20 rounded-full flex items-center justify-center mb-4">
              <WifiOff className="h-8 w-8 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Readings Unavailable Offline
            </h2>
            <p className="text-gray-400 mb-6">
              Tarot readings require an active internet connection for AI-powered interpretations.
              Please connect to the internet to get a new reading.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/history')}
                className="w-full px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                View Past Readings
              </button>
              <button
                onClick={() => navigate('/app')}
                className="w-full px-6 py-3 bg-dark-700 text-white rounded-lg hover:bg-dark-600 transition-colors"
              >
                Return to Dashboard
              </button>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4 mb-4">
            {['question', 'spread', 'drawing', 'generating', 'result'].map((step, index) => {
              const isActive = session.currentStep === step;
              const stepIndex = ['question', 'spread', 'drawing', 'generating', 'result'].indexOf(session.currentStep);
              const isCompleted = stepIndex > index;
              
              return (
                <div key={step} className="flex items-center">
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
                    ${isActive ? 'bg-primary-500 text-white' : 
                      isCompleted ? 'bg-green-500 text-white' : 'bg-dark-700 text-gray-400'}
                  `}>
                    {isCompleted ? '✓' : index + 1}
                  </div>
                  {index < 4 && (
                    <div className={`w-8 h-0.5 mx-2 ${
                      isCompleted ? 'bg-green-500' : 'bg-dark-700'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-center text-sm text-gray-400">
            Step {['question', 'spread', 'drawing', 'generating', 'result'].indexOf(session.currentStep) + 1} of 5
          </div>
        </div>
        
        {/* Question Step */}
        {session.currentStep === 'question' && (
          <div className="max-w-2xl mx-auto">
            <Card>
              <div className="text-center mb-6">
                <h1 className="text-3xl font-bold text-white mb-2">
                  Ask Your Question
                </h1>
                <p className="text-gray-400">
                  Focus on what you'd like guidance about. Be specific and open to insight.
                </p>
              </div>
              
              <form onSubmit={handleQuestionSubmit} className="space-y-6">
                <Input
                  label="Your Question"
                  value={session.question}
                  onChange={(e) => setSession(prev => ({ 
                    ...prev, 
                    question: e.target.value 
                  }))}
                  placeholder="What do I need to know about..."
                  fullWidth
                  helperText="Take a moment to center yourself and ask from the heart"
                />
                
                <div className="text-center">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={!session.question.trim()}
                  >
                    Continue to Spreads →
                  </Button>
                </div>
              </form>
              
              {/* Example Questions */}
              <div className="mt-8 pt-6 border-t border-dark-700">
                <h3 className="text-sm font-semibold text-gray-400 mb-3">
                  Example Questions:
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {[
                    "What do I need to know about my career path?",
                    "How can I improve my relationships?",
                    "What should I focus on this month?",
                    "What is blocking my personal growth?"
                  ].map((example, index) => (
                    <button
                      key={index}
                      type="button"
                      className="text-left text-gray-400 hover:text-primary-400 transition-colors"
                      onClick={() => setSession(prev => ({ 
                        ...prev, 
                        question: example 
                      }))}
                    >
                      "{example}"
                    </button>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        )}
        
        {/* Spread Selection Step */}
        {session.currentStep === 'spread' && (
          <div>
            <div className="mb-6 text-center">
              <Button
                variant="ghost"
                onClick={handleBackToQuestion}
                className="mb-4"
              >
                ← Back to Question
              </Button>
              <div className="bg-dark-800 rounded-lg p-4 max-w-2xl mx-auto">
                <h3 className="text-white font-semibold mb-1">Your Question:</h3>
                <p className="text-gray-300 italic">"{session.question}"</p>
              </div>
            </div>
            
            <SpreadSelectorV2
              selectedSpreadKey={session.selectedSpreadKey}
              onSelectSpread={handleSpreadSelect}
            />
          </div>
        )}
        
        {/* Card Drawing Step */}
        {session.currentStep === 'drawing' && (
          <CardDrawing
            spread={session.selectedSpread}
            deck={TAROT_DECK}
            onDrawingComplete={handleDrawingComplete}
            onBack={handleBackToSpread}
          />
        )}
        
        {/* AI Generation Step */}
        {session.currentStep === 'generating' && session.readingId && (
          <ReadingGenerationStatus
            readingId={session.readingId}
            itemDeducted={session.itemDeducted || false}
            onComplete={handleGenerationComplete}
            onError={handleGenerationError}
          />
        )}
        
        {/* Reading Result Step */}
        {session.currentStep === 'result' && (
          <ReadingResult
            cards={session.drawnCards}
            spread={session.selectedSpread}
            question={session.question}
            readingId={session.readingId}
            onSaveReading={handleSaveReading}
            onNewReading={handleNewReading}
          />
        )}
      </div>
    </Layout>
  );
}